/**
 * prefetch-chunks.ts — intent-based JS chunk prefetch helpers (F4).
 *
 * Background: the Workspace and TipTapEditor are behind `React.lazy`
 * boundaries in `routes/app.tsx:74-76` and `features/workspace/Workspace.tsx:99-101`.
 * On a cold visit the browser only downloads those chunks when the user clicks a
 * recipe (Workspace) or the editor mounts (TipTapEditor) — adding a serial two-hop
 * waterfall to recipe open time.
 *
 * These helpers fire side-effect `import()` calls so the browser starts
 * downloading the chunks into its HTTP cache before the user has clicked. The
 * dynamic import is idempotent: a second call while the first is in flight dedupes
 * at the module loader level and resolves against the already-in-progress fetch.
 * A second call after the chunk is cached resolves instantly.
 *
 * Two call sites:
 *   1. `LibraryView` mount — idle priority via `requestIdleCallback` (F4 trigger 1).
 *   2. `RecipeCard` / `FlatRecipeList` hover — high priority, immediate (F4 trigger 2).
 *
 * Connection gates:
 *   - `navigator.connection?.saveData === true` → skip entirely (user opted out of
 *     background data; respect it even on high-priority hover path).
 *   - `navigator.connection?.effectiveType === 'slow-2g'` → skip idle-mount path
 *     only. High-priority hover fires regardless (the user has expressed intent;
 *     the chunk would download on click anyway).
 *
 * Idempotency guard:
 *   Each function tracks whether it has been called before via a module-level flag.
 *   This prevents double-fire on React StrictMode double-mount in development and
 *   on any production re-mount scenario. The guard is module-level (not per-component)
 *   because the chunk download is global — once it's in the browser cache there is
 *   nothing to repeat.
 *
 * No new dependency — `requestIdleCallback` is built-in; the `setTimeout` fallback
 * is one line.
 */

let workspacePrefetchFired = false;
let editorPrefetchFired = false;

/**
 * Returns true when the connection is flagged as metered (saveData) or very slow.
 * Used to gate background prefetch — the idle-mount trigger uses both checks;
 * the hover trigger only checks `saveData` (intent expressed).
 */
function isMetered(): boolean {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const conn = (navigator as any).connection;
    return conn?.saveData === true;
  } catch {
    return false;
  }
}

function isSlowConnection(): boolean {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const conn = (navigator as any).connection;
    return conn?.effectiveType === "slow-2g";
  } catch {
    return false;
  }
}

/**
 * Prefetch the Workspace chunk.
 *
 * The import path must match exactly what `routes/app.tsx:74-76` passes to
 * `React.lazy` so Vite dedupes the module in its internal registry and the
 * downloaded bytes are reused by the lazy boundary — the lazy boundary resolves
 * from the module cache rather than fetching again.
 *
 * Note: the specifier matches exactly the one in `routes/app.tsx:74-76`
 * (`~/features/workspace/Workspace`) so Vite resolves both to the same chunk
 * URL and the browser's module cache dedupes them.
 */
export function prefetchWorkspace(): void {
  if (workspacePrefetchFired) return;
  if (isMetered()) return;
  workspacePrefetchFired = true;
  // Fire-and-forget. A failed prefetch is invisible to the user — the click
  // path will fetch the chunk normally. Log at debug level for diagnostics.
  void import("~/features/workspace/Workspace").catch((err: unknown) => {
    console.debug({ event: "prefetch_workspace_failed", err });
    // Reset flag so a transient network error doesn't permanently skip the
    // prefetch in a long-lived session.
    workspacePrefetchFired = false;
  });
}

/**
 * Prefetch the TipTapEditor chunk.
 *
 * Same dedup guarantee as `prefetchWorkspace` — uses the exact specifier from
 * `features/workspace/Workspace.tsx:99-101`.
 */
export function prefetchEditor(): void {
  if (editorPrefetchFired) return;
  if (isMetered()) return;
  editorPrefetchFired = true;
  void import("~/features/editor/TipTapEditor").catch((err: unknown) => {
    console.debug({ event: "prefetch_editor_failed", err });
    editorPrefetchFired = false;
  });
}

/**
 * Fire both chunk prefetches at idle priority (F4 trigger 1 — Library mount).
 *
 * Gates on both `saveData` AND `effectiveType !== 'slow-2g'` so background
 * prefetch on a metered or very-slow connection is suppressed entirely.
 *
 * Uses `requestIdleCallback` with a 500 ms timeout so the browser picks up the
 * downloads during its own idle time — it never races with critical-path
 * resources, but the deadline keeps the chunks warm before the user's first
 * click rather than after it. Falls back to `setTimeout(fn, 200)` in
 * environments that do not support `requestIdleCallback` (Safari < 16.4,
 * older WebViews).
 */
export function prefetchChunksOnIdle(): void {
  if (typeof window === "undefined") return;
  if (isMetered()) return;
  if (isSlowConnection()) return;

  const fn = () => {
    prefetchWorkspace();
    prefetchEditor();
  };

  if (typeof requestIdleCallback !== "undefined") {
    requestIdleCallback(fn, { timeout: 500 });
  } else {
    setTimeout(fn, 200);
  }
}

/**
 * Fire both chunk prefetches immediately (F4 trigger 2 — recipe-row hover).
 *
 * Only gates on `saveData`; slow-2g is intentionally NOT blocked here because
 * the user has expressed intent by hovering. On a slow connection the chunk
 * would have downloaded on click anyway — prefetching it on hover strictly
 * reduces the wait.
 */
export function prefetchChunksOnIntent(): void {
  if (typeof window === "undefined") return;
  if (isMetered()) return;
  prefetchWorkspace();
  prefetchEditor();
}
